import { Control, FieldPath, UseFormSetValue, useFieldArray, useFormState, useWatch } from "react-hook-form";

import { DecimalInput } from "@/components/form-control/decimal-input";
import { CloseIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import {
  FeeConfigFormValues,
  complementShare,
  createEmptyCatchAll,
  createEmptyTier,
  formatFrNumber,
  hasMaxTwoDecimals,
} from "../../fee-config.const";

type FeeRuleFieldsProps = {
  control: Control<FeeConfigFormValues>;
  setValue: UseFormSetValue<FeeConfigFormValues>;
  ruleIndex: number;
  onRemove?: () => void;
};

const TIER_GRID = "grid grid-cols-[1fr_1fr_1fr_1fr_2rem] gap-2";

const NumberCell = ({
  control,
  name,
}: {
  control: Control<FeeConfigFormValues>;
  name: FieldPath<FeeConfigFormValues>;
}) => (
  <FormField
    control={control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormControl>
          <DecimalInput
            value={typeof field.value === "number" ? field.value : undefined}
            onValueChange={field.onChange}
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    )}
  />
);

export const FeeRuleFields = ({ control, setValue, ruleIndex, onRemove }: FeeRuleFieldsProps) => {
  const {
    fields: tierFields,
    append: appendTier,
    remove: removeTier,
  } = useFieldArray({ control, name: `rules.${ruleIndex}.tiers` });

  const { errors } = useFormState({ control, name: `rules.${ruleIndex}.tiers` });
  const tiersErrors = errors.rules?.[ruleIndex]?.tiers;
  const tiersMessage = tiersErrors?.root?.message ?? tiersErrors?.message;

  const catchAllEnabled = useWatch({ control, name: `rules.${ruleIndex}.catchAllEnabled` });
  const overrideShares = useWatch({ control, name: `rules.${ruleIndex}.overrideShares` });
  const ruleCustomerShare = useWatch({ control, name: `rules.${ruleIndex}.customerShare` });
  const configCustomerShare = useWatch({ control, name: "customerShare" });
  const tiers = useWatch({ control, name: `rules.${ruleIndex}.tiers` });

  const lastBound = tiers?.length ? tiers[tiers.length - 1]?.upTo : undefined;
  const beyondLabel =
    tiers?.length === 0 || tiers === undefined
      ? "Sur tout prix"
      : typeof lastBound === "number" && lastBound > 0
        ? `Au-delà de ${formatFrNumber(lastBound)} €`
        : "Au-delà de la dernière tranche";

  return (
    <div className="rounded-lg border border-border p-4 space-y-4">
      {onRemove && (
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Règle {ruleIndex + 1}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="text-muted-foreground hover:text-destructive px-2"
          >
            <CloseIcon className="h-3 w-3" />
            Supprimer la règle
          </Button>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        La commission est un pourcentage du prix du stationnement, défini par tranche de prix. « Minimum » et « Maximum
        » encadrent le montant obtenu pour la tranche.
      </p>

      {(tierFields.length > 0 || catchAllEnabled) && (
        <div className={`${TIER_GRID} text-xs font-medium text-muted-foreground`}>
          <span>Tranche de prix</span>
          <span>Commission (%)</span>
          <span>Minimum (€)</span>
          <span>Maximum (€)</span>
          <span />
        </div>
      )}

      {tierFields.map((tierField, tierIndex) => (
        <div key={tierField.id} className={`${TIER_GRID} items-start`}>
          <FormField
            control={control}
            name={`rules.${ruleIndex}.tiers.${tierIndex}.upTo`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-xs text-muted-foreground pointer-events-none">
                      jusqu'à
                    </span>
                    <DecimalInput
                      className="pl-14 pr-6"
                      value={typeof field.value === "number" ? field.value : undefined}
                      onValueChange={field.onChange}
                    />
                    <span className="absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground pointer-events-none">
                      €
                    </span>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <NumberCell control={control} name={`rules.${ruleIndex}.tiers.${tierIndex}.feePercent`} />
          <NumberCell control={control} name={`rules.${ruleIndex}.tiers.${tierIndex}.minFee`} />
          <NumberCell control={control} name={`rules.${ruleIndex}.tiers.${tierIndex}.maxFee`} />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeTier(tierIndex)}
            className="text-muted-foreground hover:text-destructive px-2"
            aria-label="Supprimer la tranche"
          >
            <CloseIcon className="h-3 w-3" />
          </Button>
        </div>
      ))}

      {catchAllEnabled && (
        <div className={`${TIER_GRID} items-start`}>
          <div className="flex h-9 items-center text-sm text-muted-foreground">{beyondLabel}</div>
          <NumberCell control={control} name={`rules.${ruleIndex}.catchAll.feePercent`} />
          <NumberCell control={control} name={`rules.${ruleIndex}.catchAll.minFee`} />
          <NumberCell control={control} name={`rules.${ruleIndex}.catchAll.maxFee`} />
          <span />
        </div>
      )}

      {tiersMessage && <p className="text-sm text-destructive">{tiersMessage}</p>}

      <div className="flex items-center gap-4">
        <Button type="button" variant="outline" size="sm" onClick={() => appendTier(createEmptyTier())}>
          + Ajouter une tranche
        </Button>

        <FormField
          control={control}
          name={`rules.${ruleIndex}.catchAllEnabled`}
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    const enabled = checked === true;
                    field.onChange(enabled);
                    setValue(`rules.${ruleIndex}.catchAll`, enabled ? createEmptyCatchAll() : undefined, {
                      shouldValidate: true,
                    });
                  }}
                />
              </FormControl>
              <FormLabel className="font-normal">Commission au-delà de la dernière tranche</FormLabel>
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name={`rules.${ruleIndex}.overrideShares`}
        render={({ field }) => (
          <FormItem className="flex flex-row items-center space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => {
                  const enabled = checked === true;
                  field.onChange(enabled);
                  setValue(
                    `rules.${ruleIndex}.customerShare`,
                    enabled ? (typeof configCustomerShare === "number" ? configCustomerShare : 100) : undefined,
                    { shouldValidate: true },
                  );
                }}
              />
            </FormControl>
            <FormLabel className="font-normal">Répartition client / ville spécifique à cette règle</FormLabel>
          </FormItem>
        )}
      />

      {overrideShares && (
        <FormField
          control={control}
          name={`rules.${ruleIndex}.customerShare`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Part client (%)</FormLabel>
              <div className="flex items-center gap-3">
                <FormControl>
                  <DecimalInput
                    className="w-28"
                    value={typeof field.value === "number" ? field.value : undefined}
                    onValueChange={field.onChange}
                  />
                </FormControl>
                {typeof ruleCustomerShare === "number" &&
                  ruleCustomerShare >= 0 &&
                  ruleCustomerShare <= 100 &&
                  hasMaxTwoDecimals(ruleCustomerShare) && (
                    <span className="text-sm text-muted-foreground">
                      Part ville : {formatFrNumber(complementShare(ruleCustomerShare))} %
                    </span>
                  )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
